import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchWithAuth } from '../../utils/api';
import RowsPerPageSelector from '../common/RowsPerPageSelector';
import { CreditCard, Edit, X } from 'lucide-react';
import Pagination from '../common/Pagination';
import ConfirmationModal from '../modals/ConfirmationModal';

function PaymentDetails({ user, handleLogout }) {
  const [details, setDetails] = useState([]);
  const [newDetail, setNewDetail] = useState({ title: '', invoice_header: '', details: '' });
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });
  const [editingDetail, setEditingDetail] = useState(null);
  const [viewingDetail, setViewingDetail] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'title', direction: 'asc' });

  const fetchDetails = useCallback(() => {
    fetchWithAuth('/payment_details', {}, user, handleLogout)
      .then(res => res.json())
      .then(data => setDetails(data || []))
      .catch(err => console.error(err));
  }, [user, handleLogout]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage, sortConfig]);

  const sortedDetails = useMemo(() => {
    let sortableItems = [...details];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [details, sortConfig]);

  const paginatedDetails = useMemo(() => {
    if (rowsPerPage === 'all') return sortedDetails;
    const start = (currentPage - 1) * rowsPerPage;
    return sortedDetails.slice(start, start + rowsPerPage);
  }, [sortedDetails, currentPage, rowsPerPage]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleCreate = (e) => {
    e.preventDefault();
    setError('');
    fetchWithAuth('/payment_details/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newDetail)
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      fetchDetails();
      setNewDetail({ title: '', invoice_header: '', details: '' });
    })
    .catch(err => setError(err.message));
  };

  const handleEditClick = (d) => {
    setEditingDetail(d);
    setError('');
  };

  const handleUpdateSubmit = (e) => {
    e.preventDefault();
    setError('');
    fetchWithAuth('/payment_details/update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editingDetail)
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      fetchDetails();
      setEditingDetail(null);
    })
    .catch(err => setError(err.message));
  };

  const handleDelete = (id) => {
    setDeleteConfirm({ isOpen: true, id });
  };

  const confirmDelete = () => {
    const id = deleteConfirm.id;
    fetchWithAuth(`/payment_details/delete?id=${id}`, {
      method: 'POST'
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      fetchDetails();
      setDeleteConfirm({ isOpen: false, id: null });
    })
    .catch(err => {
      setError(err.message);
      setDeleteConfirm({ isOpen: false, id: null });
    });
  };

  return (
    <div className="view-container">
      <div className="view-header">
        <h2>Payment Details</h2>
      </div>

      {error && <div className="error-message">{error}</div>}
      
      <div className="list-controls">
        <RowsPerPageSelector value={rowsPerPage} onChange={setRowsPerPage} />
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th onClick={() => requestSort('title')} className="sortable">Title {sortConfig.key === 'title' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
              <th>Details Preview</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedDetails.map(d => (
              <tr key={d.id} onClick={() => setViewingDetail(d)} className="clickable-row">
                <td><strong>{d.title}</strong></td>
                <td style={{maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-secondary)'}}>{d.details}</td>
                <td onClick={e => e.stopPropagation()} className="text-right">
                  <div className="row-actions">
                    <button className="action-btn sm" onClick={() => handleEditClick(d)}>Edit</button>
                    <button className="cancel-btn sm" onClick={() => handleDelete(d.id)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {paginatedDetails.length === 0 && (
              <tr>
                <td colSpan="3" className="text-center" style={{padding: '2rem', color: 'var(--text-secondary)'}}>No payment details found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination 
        totalItems={details.length} 
        itemsPerPage={rowsPerPage} 
        currentPage={currentPage} 
        onPageChange={setCurrentPage} 
      />

      <div className="form-section">
        <h3>Add New Payment Detail</h3>
        <form onSubmit={handleCreate} style={{marginTop: '1.5rem'}}>
          <div className="form-row">
            <div className="form-group">
              <label>Title</label>
              <input value={newDetail.title} onChange={e => setNewDetail({...newDetail, title: e.target.value})} placeholder="e.g. Bank Transfer (BDO)" required />
            </div>
            <div className="form-group">
              <label>Invoice Header</label>
              <input value={newDetail.invoice_header} onChange={e => setNewDetail({...newDetail, invoice_header: e.target.value})} placeholder="e.g. NOKNOK FARM INVOICE" />
            </div>
          </div>
          <div className="form-group">
            <label>Details</label>
            <textarea 
              value={newDetail.details} 
              onChange={e => setNewDetail({...newDetail, details: e.target.value})} 
              placeholder="Enter account name, number, and other instructions..."
              required 
            />
          </div>
          <div className="form-actions">
            <button type="submit" className="submit-btn">Add Payment Detail</button>
          </div>
        </form>
      </div>

      <ConfirmationModal 
        isOpen={deleteConfirm.isOpen}
        title="Delete Payment Detail"
        message="Are you sure you want to delete this payment detail?"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null })}
      />

      {editingDetail && (
        <div className="modal" onClick={() => { setEditingDetail(null); setError(''); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <Edit size={24} />
                <h3>Edit Payment Detail</h3>
              </div>
              <button className="close-btn" onClick={() => { setEditingDetail(null); setError(''); }}><X size={20} /></button>
            </div>
            {error && <div className="error-message" style={{marginBottom: '1rem'}}>{error}</div>}
            <form onSubmit={handleUpdateSubmit}>
              <div className="form-group">
                <label>Title</label>
                <input 
                  value={editingDetail.title} 
                  onChange={e => setEditingDetail({...editingDetail, title: e.target.value})} 
                  required 
                />
              </div>
              <div className="form-group">
                <label>Invoice Header</label>
                <input 
                  value={editingDetail.invoice_header} 
                  onChange={e => setEditingDetail({...editingDetail, invoice_header: e.target.value})} 
                />
              </div>
              <div className="form-group">
                <label>Details</label>
                <textarea 
                  value={editingDetail.details} 
                  onChange={e => setEditingDetail({...editingDetail, details: e.target.value})} 
                  required 
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="submit-btn">Update</button>
                <button type="button" className="secondary-btn" onClick={() => { setEditingDetail(null); setError(''); }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewingDetail && (
        <div className="modal" onClick={() => setViewingDetail(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <CreditCard size={24} />
                <h3>{viewingDetail.title}</h3>
              </div>
              <button className="close-btn" onClick={() => setViewingDetail(null)}><X size={20} /></button>
            </div>
            {viewingDetail.invoice_header && (
              <p style={{marginBottom: '1.5rem', color: 'var(--text-secondary)'}}>
                <strong>Header:</strong> {viewingDetail.invoice_header}
              </p>
            )}
            <div style={{whiteSpace: 'pre-wrap', padding: '1.5rem', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid var(--border-color)', color: 'var(--text-primary)', fontSize: '0.95rem', lineHeight: '1.6'}}>
              {viewingDetail.details}
            </div>
            <div className="form-actions" style={{marginTop: '2rem'}}>
              <button onClick={() => setViewingDetail(null)} className="secondary-btn">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PaymentDetails;
